import * as React from "react";

import styles from "./ResourceViewEmptyState.module.css";

/**
 * What an empty resource table shows in place of rows: an icon, a title, a
 * line of explanation and a call to action. The same shape describes both
 * the "nothing here yet" and the "nothing matches" cases of
 * `<ResourceView.EmptyState>`.
 */
export interface ResourceViewEmptyStateContent {
  /**
   * Icon drawn above the title, usually the lucide icon the sidebar uses for
   * this resource. Rendered muted at a fixed size and hidden from assistive
   * technology, since the title carries the meaning.
   */
  icon?: React.ComponentType<{
    className?: string;
    "aria-hidden"?: React.AriaAttributes["aria-hidden"];
  }>;
  /** One short line naming what is missing, e.g. "No devices yet". No trailing period. */
  title: React.ReactNode;
  /**
   * Optional second line: how rows come to exist, or, when a filter is
   * active, that loosening it is the way forward. Takes markup, so a caller
   * can link or bold a word.
   */
  description?: React.ReactNode;
  /**
   * Optional call to action under the text, typically the resource's create
   * button in its `secondary` variant so it reads as an invitation rather
   * than competing with the toolbar's primary button.
   */
  action?: React.ReactNode;
}

export interface ResourceViewEmptyStateProps {
  /**
   * `view.hasActiveFilters`. When true the table is empty because of the
   * filters, so `noMatches` shows; otherwise the resource has no rows at all
   * and `noData` shows.
   */
  hasActiveFilters: boolean;
  /** What to show when the resource has no rows at all. */
  noData: ResourceViewEmptyStateContent;
  /** What to show when the active filters match no rows. */
  noMatches: ResourceViewEmptyStateContent;
}

/**
 * What a resource table shows in place of rows when there are none. Pass it
 * as the `<DataTable>`'s `noRowsContent` with the view's `hasActiveFilters`,
 * and it picks between the two cases itself, so no list can forget that an
 * empty filtered result is not an empty org.
 *
 * @example
 * ```tsx
 * <DataTable
 *   noRowsContent={
 *     <ResourceView.EmptyState
 *       hasActiveFilters={view.hasActiveFilters}
 *       noData={{
 *         icon: BotIcon,
 *         title: "No devices yet",
 *         description: "Register the robots and systems that send data to Roboto.",
 *         action: <CreateDeviceButton variant="secondary" />,
 *       }}
 *       noMatches={{
 *         icon: BotIcon,
 *         title: "No matching devices",
 *         description: "Try adjusting or clearing your filters.",
 *       }}
 *     />
 *   }
 * />
 * ```
 */
export function ResourceViewEmptyState({
  hasActiveFilters,
  noData,
  noMatches,
}: ResourceViewEmptyStateProps) {
  const {
    icon: Icon,
    title,
    description,
    action,
  } = hasActiveFilters ? noMatches : noData;
  return (
    <div className={styles.emptyState}>
      {Icon && <Icon className={styles.icon} aria-hidden />}
      <div className={styles.text}>
        <p className={styles.title}>{title}</p>
        {description !== undefined && (
          <p className={styles.description}>{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}
